import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo
} from 'react';
import routes from '@routes';
import { Content } from '@layout';
import useLayoutContext from '@hooks/useLayoutContext';
import usePageContext from '@hooks/usePageContext';
import logoWhite from '@images/logo-white.svg';
import logo from '@images/logo.svg';
import styles, { mobileSize } from './Navigation.module.scss';
import { useMeasure } from 'react-use';
import { Link } from 'gatsby';
import 'focus-within-polyfill';
import classNames from 'classnames';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';

export default React.memo(function Navigation({ overlayNav }) {
  const { isMobile, mobileMenuIsOpen, updateLayout } = useLayoutContext();
  const mobileMenuToggle = useRef();
  const { pathname } = usePageContext().location;
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    mobileMenuToggle.current?.blur();
  }, [mobileMenuIsOpen]);

  const navEvents = {
    onClick() {
      document.activeElement?.blur();
    }
  };

  if (!hasInteracted) {
    const handler = () => setHasInteracted(true);
    navEvents.onMouseOut = handler;
    navEvents.onMouseEnter = handler;
    navEvents.onTouchStart = handler;
  }

  const links = useMemo(
    () =>
      routes
        .filter(({ to }) => to !== '/')
        .map((item, index) => {
          const isActive =
            pathname === '/lab/aradhna-tripati'
              ? item.to === 'about'
              : pathname.replace(/^\//, '').split('/')[0] ===
                item.to.split('/')[0];

          return (
            <NavItem
              key={index}
              isActive={isActive}
              isMobile={isMobile}
              mobileMenuIsOpen={mobileMenuIsOpen}
              {...item}
            />
          );
        }),
    [isMobile, mobileMenuIsOpen, pathname]
  );

  return (
    <nav
      className={classNames(styles.wrapper, {
        [styles.overlay]: overlayNav,
        [styles.hideSubmenu]: !hasInteracted
      })}
      {...navEvents}
    >
      <RemoveScroll enabled={isMobile && mobileMenuIsOpen}>
        <Content className={styles.content}>
          <Link
            to="/"
            className={styles.logo}
          >
            <img
              src={overlayNav ? logoWhite : logo}
              alt="Tripati Lab"
            />
          </Link>
          <FocusLock
            autoFocus={false}
            disabled={!isMobile || !mobileMenuIsOpen}
          >
            {isMobile && (
              <button
                ref={mobileMenuToggle}
                className={classNames(styles.mobileMenuToggle, {
                  [styles.open]: mobileMenuIsOpen
                })}
                aria-label="Toggle menu"
                onClick={() => {
                  updateLayout({ mobileMenuIsOpen: !mobileMenuIsOpen });
                }}
              >
                <span>
                  <span />
                </span>
              </button>
            )}
            <div
              className={classNames(styles.navWrapper, {
                [styles.open]: isMobile && mobileMenuIsOpen
              })}
            >
              <div
                className={styles.mobileTitle}
                aria-hidden="true"
              >
                <span>Tripati Lab</span>
              </div>
              <ul className={styles.nav}>{links}</ul>
            </div>
          </FocusLock>
        </Content>
      </RemoveScroll>
    </nav>
  );
});

const NavItem = React.memo(
  ({ to, text, subLinks, isActive, isMobile, mobileMenuIsOpen }) => {
    return (
      <li>
        <Link
          to={`/${to}`}
          className={classNames({
            [styles.hasToggle]: subLinks,
            [styles.active]: isActive
          })}
          tabIndex={!isMobile || mobileMenuIsOpen ? 0 : -1}
        >
          {text}
        </Link>
        {subLinks && (
          <SubMenu
            parentTo={to}
            isMobile={isMobile}
            mobileMenuIsOpen={mobileMenuIsOpen}
            links={subLinks}
          />
        )}
        {isMobile && <div className={styles.divider} />}
      </li>
    );
  }
);

const SubMenu = React.memo(
  ({ parentTo, links, isMobile, mobileMenuIsOpen }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [ref, { height: subMenuHeight }] = useMeasure();
    const [height, setHeight] = useState(null);

    const setSubmenuHeight = useCallback(() => {
      setHeight(!isMobile || !isExpanded ? null : subMenuHeight);
    }, [isMobile, isExpanded, subMenuHeight]);

    useEffect(setSubmenuHeight, [setSubmenuHeight]);

    useEffect(() => {
      if (!isMobile) {
        setIsExpanded(false);
      }
    }, [isMobile]);

    const toggleExpanded = useCallback(() => {
      setIsExpanded(isExpanded => !isExpanded);
    }, []);

    const handleKeyDown = useCallback(
      e => {
        if (e.key === 'Enter' || e.key === ' ') {
          toggleExpanded();
        }
      },
      [toggleExpanded]
    );

    const items = useMemo(
      () =>
        links.map(({ to, text }, index) => (
          <li key={index}>
            <Link
              to={to?.match(/^\//) ? to : `/${parentTo}/${to}`}
              tabIndex={
                isMobile && (!mobileMenuIsOpen || !isExpanded) ? -1 : 0
              }
              activeClassName={styles.active}
            >
              {text}
            </Link>
          </li>
        )),
      [isExpanded, isMobile, links, mobileMenuIsOpen, parentTo]
    );

    return (
      <React.Fragment>
        {isMobile && (
          <button
            className={classNames(styles.expandToggle, {
              [styles.expanded]: isExpanded
            })}
            onMouseDown={toggleExpanded}
            onKeyDown={handleKeyDown}
            aria-label="Expand submenu"
            tabIndex={mobileMenuIsOpen ? 0 : -1}
          >
            <span className={styles.expandIcon} />
          </button>
        )}
        <div
          style={{ maxHeight: !isMobile || !isExpanded ? null : height }}
          className={classNames(styles.subMenuWrapper, {
            [styles.expanded]: isExpanded
          })}
        >
          <ul
            ref={ref}
            className={styles.subMenu}
          >
            {items}
          </ul>
        </div>
      </React.Fragment>
    );
  }
);

export { mobileSize };
